import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Skeleton } from "../components/ui/skeleton.jsx";
import { User } from '../api/supabaseEntities.js';
import { Ticket } from '../api/supabaseEntities.js';
import { Company } from '../api/supabaseEntities.js';
import { TicketComment } from '../api/supabaseEntities.js';
import { Reports as ReportsAPI } from '../api/supabaseEntities.js';
import { BarChart, LineChart, PieChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Bar, Line, Pie, Cell } from 'recharts';
import { DownloadIcon, FilterIcon, Calendar, RefreshCw } from 'lucide-react';
import { format, subDays, subMonths, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Corrigindo o import do DateRangeFilter
import DateRangeFilter from '../components/common/DateRangeFilter.jsx';
import ExportButton from '../components/common/ExportButton.jsx';

export default function Reports() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [reportType, setReportType] = useState('tickets');
  const [dateRange, setDateRange] = useState('last30');
  const [reportData, setReportData] = useState({
    tickets: null,
    performance: null
  });

  // Cores para os gráficos
  const COLORS = ['#6366F1', '#60A5FA', '#34D399', '#FBBF24', '#F87171', '#A78BFA'];

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadReportData();
    }
  }, [currentUser, reportType, dateRange]);

  const loadUserData = async () => {
    try {
      const userData = await User.me();
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const calculateDateRange = () => {
    let startDate;
    let endDate = new Date();
    
    switch (dateRange) {
      case 'last7':
        startDate = subDays(new Date(), 7);
        break;
      case 'last30':
        startDate = subDays(new Date(), 30);
        break;
      case 'last90':
        startDate = subDays(new Date(), 90);
        break;
      case 'thisMonth':
        startDate = startOfMonth(new Date());
        break;
      case 'lastMonth':
        startDate = startOfMonth(subMonths(new Date(), 1));
        endDate = endOfMonth(subMonths(new Date(), 1));
        break;
      default:
        startDate = subDays(new Date(), 30);
    }
    
    return [startDate, endDate];
  };

  const loadReportData = async () => {
    setIsLoading(true);
    
    try {
      const [startDate, endDate] = calculateDateRange();
      console.log('📊 Carregando dados de relatório...', { startDate, endDate, reportType });
      
      // Verificar se temos acesso às funções RPC otimizadas
      const companyId = currentUser?.role === 'client' ? currentUser.company_id : null;
      
      if (reportType === 'tickets') {
        console.log('🎯 Processando relatório de chamados...');
        
        try {
          // Tentar usar funções RPC otimizadas primeiro
          const [generalStats, statusStats, priorityStats] = await Promise.allSettled([
            ReportsAPI.getTicketsStatsByPeriod(startDate?.toISOString(), endDate?.toISOString(), companyId),
            ReportsAPI.getTicketsByStatusStats(startDate?.toISOString(), endDate?.toISOString(), companyId),
            ReportsAPI.getTicketsByPriorityStats(startDate?.toISOString(), endDate?.toISOString(), companyId)
          ]);
          
          if (generalStats.status === 'fulfilled' && statusStats.status === 'fulfilled' && priorityStats.status === 'fulfilled') {
            console.log('✅ Usando funções RPC otimizadas');
            
            // Processar dados das funções RPC
            const stats = generalStats.value;
            const byStatus = statusStats.value.map(item => ({
              name: item.status_label,
              value: item.count
            }));
            const byPriority = priorityStats.value.map(item => ({
              name: item.priority_label,
              valor: item.count
            }));
            
            // Carregar tickets e atividades para o gráfico por dia
            const [ticketsData, commentsData] = await Promise.allSettled([
              Ticket.list(),
              TicketComment.list()
            ]);
            
            const tickets = ticketsData.status === 'fulfilled' ? ticketsData.value || [] : [];
            const comments = commentsData.status === 'fulfilled' ? commentsData.value || [] : [];
            
            // Filtrar dados pelo período
            const filteredTickets = tickets.filter(ticket => {
              const ticketDate = new Date(ticket.created_at);
              return ticketDate >= startDate && ticketDate <= endDate;
            });
            
            const filteredComments = comments.filter(comment => {
              const commentDate = new Date(comment.created_at);
              return commentDate >= startDate && commentDate <= endDate;
            });
            
            // Para o gráfico por dia, sempre usar últimos 7 dias
            const last7DaysStart = subDays(new Date(), 7);
            const last7DaysEnd = new Date();
            
            const last7DaysTickets = tickets.filter(ticket => {
              const ticketDate = new Date(ticket.created_at);
              return ticketDate >= last7DaysStart && ticketDate <= last7DaysEnd;
            });
            
            const last7DaysComments = comments.filter(comment => {
              const commentDate = new Date(comment.created_at);
              return commentDate >= last7DaysStart && commentDate <= last7DaysEnd;
            });
            
            const byDay = processTicketsByDayWithActivities(last7DaysTickets, last7DaysComments, last7DaysStart, last7DaysEnd);
            
            setReportData({
              ...reportData,
              tickets: {
                byStatus,
                byPriority,
                byDay,
                slaCompliance: stats.sla_compliance_percentage,
                avgResolutionTime: parseFloat(stats.avg_resolution_hours),
                totalNew: stats.new_tickets,
                totalClosed: stats.closed_tickets
              }
            });
            
            setIsLoading(false);
            return;
          }
        } catch (rpcError) {
          console.warn('⚠️ Funções RPC não disponíveis, usando método de fallback:', rpcError);
        }
        
        // Fallback: Carregar dados usando método anterior
        console.log('🔄 Usando método de fallback para relatório de tickets...');
        const [ticketsData, usersData, companiesData, commentsData] = await Promise.allSettled([
          Ticket.list(),
          User.list(),
          Company.list(),
          TicketComment.list()
        ]);

        const tickets = ticketsData.status === 'fulfilled' ? ticketsData.value || [] : [];
        const users = usersData.status === 'fulfilled' ? usersData.value || [] : [];
        const companies = companiesData.status === 'fulfilled' ? companiesData.value || [] : [];
        const comments = commentsData.status === 'fulfilled' ? commentsData.value || [] : [];

        console.log('📋 Dados carregados (fallback):', {
          tickets: tickets.length,
          users: users.length,
          companies: companies.length,
          comments: comments.length
        });

        // Filtrar tickets pelo período selecionado
        const filteredTickets = tickets.filter(ticket => {
          const ticketDate = new Date(ticket.created_at);
          return ticketDate >= startDate && ticketDate <= endDate;
        });
        
        // Filtrar comentários pelo período selecionado
        const filteredComments = comments.filter(comment => {
          const commentDate = new Date(comment.created_at);
          return commentDate >= startDate && commentDate <= endDate;
        });
        
        // Dados reais para o relatório de chamados
        const ticketsByStatus = processTicketsByStatus(filteredTickets);
        const ticketsByPriority = processTicketsByPriority(filteredTickets);
        // Para o gráfico por dia, sempre usar últimos 7 dias
        const last7DaysStart = subDays(new Date(), 7);
        const last7DaysEnd = new Date();
        
        const last7DaysTickets = tickets.filter(ticket => {
          const ticketDate = new Date(ticket.created_at);
          return ticketDate >= last7DaysStart && ticketDate <= last7DaysEnd;
        });
        
        const last7DaysComments = comments.filter(comment => {
          const commentDate = new Date(comment.created_at);
          return commentDate >= last7DaysStart && commentDate <= last7DaysEnd;
        });
        
        const ticketsByDay = processTicketsByDayWithActivities(last7DaysTickets, last7DaysComments, last7DaysStart, last7DaysEnd);
        const slaStats = calculateSlaStats(filteredTickets);
        const resolutionStats = calculateResolutionStats(filteredTickets);
        
        setReportData({
          ...reportData,
          tickets: {
            byStatus: ticketsByStatus,
            byPriority: ticketsByPriority,
            byDay: ticketsByDay,
            slaCompliance: slaStats.compliance,
            avgResolutionTime: resolutionStats.avgTime,
            totalNew: filteredTickets.length,
            totalClosed: filteredTickets.filter(t => ['resolved', 'closed'].includes(t.status)).length
          }
        });
      }
      else if (reportType === 'performance') {
        console.log('📈 Processando relatório de performance...');
        
        try {
          // Tentar usar função RPC otimizada primeiro
          const userStats = await ReportsAPI.getUserPerformanceStats(
            startDate?.toISOString(), 
            endDate?.toISOString()
          );
          
          console.log('✅ Usando função RPC otimizada para performance');
          
          // Processar dados da função RPC
          const userPerformance = userStats.map(user => ({
            name: user.user_name || user.user_email,
            resolvidos: user.total_resolved,
            avaliacao: 4.2 // Valor padrão
          }));
          
          const slaByUser = userStats.map(user => ({
            name: user.user_name || user.user_email,
            cumprido: user.sla_compliance_percentage,
            violado: 100 - user.sla_compliance_percentage
          }));
          
          const avgResolutionTime = userStats.length > 0 
            ? userStats.reduce((sum, user) => sum + parseFloat(user.avg_resolution_hours), 0) / userStats.length
            : 0;
          
          setReportData({
            ...reportData,
            performance: {
              userPerformance,
              slaByUser,
              avgRating: 4.2,
              avgResolutionTime: Math.round(avgResolutionTime * 10) / 10
            }
          });
          
        } catch (rpcError) {
          console.warn('⚠️ Função RPC de performance não disponível, usando fallback:', rpcError);
          
          // Fallback: método anterior
          const [ticketsData, usersData] = await Promise.allSettled([
            Ticket.list(),
            User.list()
          ]);

          const tickets = ticketsData.status === 'fulfilled' ? ticketsData.value || [] : [];
          const users = usersData.status === 'fulfilled' ? usersData.value || [] : [];

          const filteredTickets = tickets.filter(ticket => {
            const ticketDate = new Date(ticket.created_at);
            return ticketDate >= startDate && ticketDate <= endDate;
          });
          
          const userPerformance = processUserPerformance(filteredTickets, users);
          const slaByUser = processSlaByUser(filteredTickets, users);
          const globalStats = calculateGlobalPerformanceStats(filteredTickets);
          
          setReportData({
            ...reportData,
            performance: {
              userPerformance,
              slaByUser,
              avgRating: globalStats.avgRating,
              avgResolutionTime: globalStats.avgResolutionTime
            }
          });
        }
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados do relatório:', error);
      
      // Dados de fallback em caso de erro
      setReportData({
        tickets: {
          byStatus: [],
          byPriority: [],
          byDay: [],
          slaCompliance: 0,
          avgResolutionTime: 0,
          totalNew: 0,
          totalClosed: 0
        },
        performance: {
          userPerformance: [],
          slaByUser: [],
          avgRating: 0,
          avgResolutionTime: 0
        }
      });
    }
    setIsLoading(false);
  };

  // Função para processar tickets por dia com atividades
  const processTicketsByDayWithActivities = (tickets, comments, startDate, endDate) => {
    const days = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'dd/MM');
      const fullDateStr = format(currentDate, 'yyyy-MM-dd');
      
      // Tickets criados no dia
      const dayTickets = tickets.filter(ticket => {
        const ticketDate = new Date(ticket.created_at);
        return format(ticketDate, 'yyyy-MM-dd') === fullDateStr;
      });

      // Atividades (comentários) do dia
      const dayActivities = comments.filter(comment => {
        const commentDate = new Date(comment.created_at);
        return format(commentDate, 'yyyy-MM-dd') === fullDateStr;
      });

      const openTickets = dayTickets.filter(t => ['open', 'analysis', 'in_progress'].includes(t.status)).length;
      const resolvedTickets = dayTickets.filter(t => ['resolved', 'closed'].includes(t.status)).length;
      const activities = dayActivities.length;

      days.push({
        data: dateStr,
        abertos: openTickets,
        resolvidos: resolvedTickets,
        atividades: activities
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  // Função de fallback para tickets por dia quando RPC não disponível
  const processTicketsByDayFallback = (startDate, endDate) => {
    const days = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'dd/MM');
      
      // Como não temos os dados detalhados, usar valores zero
      days.push({
        data: dateStr,
        abertos: 0,
        resolvidos: 0,
        atividades: 0
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  // Função para processar tickets por status
  const processTicketsByStatus = (tickets) => {
    // Mapear todos os status encontrados
    const statusCounts = {};
    tickets.forEach(ticket => {
      const status = ticket.status || 'open';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    // Legendas conhecidas
    const statusLabels = {
      open: 'Abertos',
      analysis: 'Em Análise',
      in_progress: 'Em Atendimento',
      resolved: 'Resolvidos',
      closed: 'Fechados',
      reopened: 'Reabertos',
      cancelled: 'Cancelados',
      pending: 'Pendentes',
      waiting: 'Aguardando',
      // Adicione outros status conhecidos aqui se necessário
    };

    // Gera lista de status únicos encontrados
    return Object.keys(statusCounts).map(status => ({
      name: statusLabels[status] || status.charAt(0).toUpperCase() + status.slice(1),
      value: statusCounts[status]
    }));
  };

  // Função para processar tickets por prioridade
  const processTicketsByPriority = (tickets) => {
    const priorityCounts = {
      low: 0,
      normal: 0,
      high: 0,
      critical: 0
    };

    tickets.forEach(ticket => {
      const priority = ticket.priority || 'normal';
      if (priorityCounts.hasOwnProperty(priority)) {
        priorityCounts[priority]++;
      } else {
        priorityCounts.normal++; // Fallback
      }
    });

    const priorityLabels = {
      low: 'Baixa',
      normal: 'Normal',
      high: 'Alta',
      critical: 'Crítica'
    };

    return Object.keys(priorityCounts).map(priority => ({
      name: priorityLabels[priority],
      valor: priorityCounts[priority]
    }));
  };

  // Função para processar tickets por dia
  const processTicketsByDay = (tickets, startDate, endDate) => {
    const days = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'dd/MM');
      const fullDateStr = format(currentDate, 'yyyy-MM-dd');
      
      const dayTickets = tickets.filter(ticket => {
        const ticketDate = new Date(ticket.created_at);
        return format(ticketDate, 'yyyy-MM-dd') === fullDateStr;
      });

      const openTickets = dayTickets.filter(t => ['open', 'analysis', 'in_progress'].includes(t.status)).length;
      const resolvedTickets = dayTickets.filter(t => ['resolved', 'closed'].includes(t.status)).length;

      days.push({
        data: dateStr,
        abertos: openTickets,
        resolvidos: resolvedTickets,
        atividades: 0 // Para compatibilidade quando não há dados de atividades
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  // Função para calcular estatísticas de SLA
  const calculateSlaStats = (tickets) => {
    if (tickets.length === 0) return { compliance: 0 };

    let withinSla = 0;
    const now = new Date();

    tickets.forEach(ticket => {
      const createdAt = new Date(ticket.created_at);
      const dueDate = ticket.due_date ? new Date(ticket.due_date) : new Date(createdAt.getTime() + (7 * 24 * 60 * 60 * 1000));
      const resolvedAt = ticket.resolved_at ? new Date(ticket.resolved_at) : now;

      if (['resolved', 'closed'].includes(ticket.status)) {
        if (resolvedAt <= dueDate) {
          withinSla++;
        }
      } else {
        if (now <= dueDate) {
          withinSla++;
        }
      }
    });

    return {
      compliance: Math.round((withinSla / tickets.length) * 100)
    };
  };

  // Função para calcular estatísticas de resolução
  const calculateResolutionStats = (tickets) => {
    const resolvedTickets = tickets.filter(t => ['resolved', 'closed'].includes(t.status) && t.resolved_at);
    
    if (resolvedTickets.length === 0) return { avgTime: 0 };

    const totalHours = resolvedTickets.reduce((sum, ticket) => {
      const created = new Date(ticket.created_at);
      const resolved = new Date(ticket.resolved_at);
      const diffHours = (resolved - created) / (1000 * 60 * 60);
      return sum + diffHours;
    }, 0);

    return {
      avgTime: Math.round((totalHours / resolvedTickets.length) * 10) / 10
    };
  };

  // Substituir a função processUserPerformance por uma versão mais completa
  const processUserPerformance = (tickets, users) => {
    const userStats = {};

    // Inicializar stats para todos os usuários internos
    users.filter(user => ['admin', 'support', 'developer'].includes(user.role))
      .forEach(user => {
        userStats[user.id] = {
          name: user.name || user.email,
          resolved: 0,
          pending: 0,
          avaliacao: 4.0 // Valor padrão já que não temos sistema de avaliação ainda
        };
      });

    // Contar tickets por usuário
    tickets.forEach(ticket => {
      if (ticket.assigned_to && userStats[ticket.assigned_to]) {
        if (["resolved", "closed"].includes(ticket.status)) {
          userStats[ticket.assigned_to].resolved++;
        } else {
          userStats[ticket.assigned_to].pending++;
        }
      }
    });

    // Retornar todos os usuários internos, mesmo sem tickets
    return Object.values(userStats)
      .sort((a, b) => (b.resolved + b.pending) - (a.resolved + a.pending))
      .slice(0, 10); // Top 10 usuários
  };

  // Função para processar SLA por usuário
  const processSlaByUser = (tickets, users) => {
    const userSlaStats = {};

    // Inicializar stats para usuários internos
    users.filter(user => ['admin', 'support', 'developer'].includes(user.role))
      .forEach(user => {
        userSlaStats[user.id] = {
          name: user.name || user.email,
          cumprido: 0,
          violado: 0
        };
      });

    // Calcular SLA por usuário
    tickets.forEach(ticket => {
      if (ticket.assigned_to && userSlaStats[ticket.assigned_to]) {
        const createdAt = new Date(ticket.created_at);
        const dueDate = ticket.due_date ? new Date(ticket.due_date) : new Date(createdAt.getTime() + (7 * 24 * 60 * 60 * 1000));
        const resolvedAt = ticket.resolved_at ? new Date(ticket.resolved_at) : new Date();

        if (['resolved', 'closed'].includes(ticket.status)) {
          if (resolvedAt <= dueDate) {
            userSlaStats[ticket.assigned_to].cumprido++;
          } else {
            userSlaStats[ticket.assigned_to].violado++;
          }
        } else {
          if (new Date() <= dueDate) {
            userSlaStats[ticket.assigned_to].cumprido++;
          } else {
            userSlaStats[ticket.assigned_to].violado++;
          }
        }
      }
    });

    return Object.values(userSlaStats)
      .filter(user => (user.cumprido + user.violado) > 0)
      .sort((a, b) => (b.cumprido + b.violado) - (a.cumprido + a.violado))
      .slice(0, 10); // Top 10 usuários
  };

  // Função para calcular estatísticas globais de performance
  const calculateGlobalPerformanceStats = (tickets) => {
    const resolvedTickets = tickets.filter(t => ['resolved', 'closed'].includes(t.status) && t.resolved_at);
    
    if (resolvedTickets.length === 0) {
      return { avgRating: 0, avgResolutionTime: 0 };
    }

    const totalHours = resolvedTickets.reduce((sum, ticket) => {
      const created = new Date(ticket.created_at);
      const resolved = new Date(ticket.resolved_at);
      const diffHours = (resolved - created) / (1000 * 60 * 60);
      return sum + diffHours;
    }, 0);

    return {
      avgRating: 4.2, // Valor padrão já que não temos sistema de avaliação ainda
      avgResolutionTime: Math.round((totalHours / resolvedTickets.length) * 10) / 10
    };
  };

  const renderTicketsReport = () => {
    if (!reportData.tickets || isLoading) {
      return (
        <div className="space-y-6">
          <Skeleton className="h-[300px] w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-[250px] w-full" />
            <Skeleton className="h-[250px] w-full" />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total de Chamados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {reportData.tickets.byStatus.reduce((acc, item) => acc + item.value, 0)}
              </div>
              <p className="text-xs text-muted-foreground">No período selecionado</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Novos Chamados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {reportData.tickets.totalNew}
              </div>
              <p className="text-xs text-muted-foreground">No período selecionado</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Chamados Fechados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {reportData.tickets.totalClosed}
              </div>
              <p className="text-xs text-muted-foreground">No período selecionado</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Cumprimento SLA</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-indigo-600">
                {reportData.tickets.slaCompliance}%
              </div>
              <p className="text-xs text-muted-foreground">Média do período</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Chamados por Status</CardTitle>
          </CardHeader>
          <CardContent className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={reportData.tickets.byStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {reportData.tickets.byStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Chamados por Prioridade</CardTitle>
            </CardHeader>
            <CardContent className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.tickets.byPriority}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="valor" fill="#6366F1" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Chamados por Dia (Últimos 7 Dias)</CardTitle>
            </CardHeader>
            <CardContent className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.tickets.byDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="data" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar 
                    dataKey="abertos" 
                    fill="#F87171" 
                    name="Chamados Abertos"
                  />
                  <Bar 
                    dataKey="resolvidos" 
                    fill="#34D399" 
                    name="Chamados Resolvidos"
                  />
                  <Bar 
                    dataKey="atividades" 
                    fill="#6366F1" 
                    name="Atividades"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderPerformanceReport = () => {
    if (!reportData.performance || isLoading) {
      return (
        <div className="space-y-6">
          <Skeleton className="h-[300px] w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-[250px] w-full" />
            <Skeleton className="h-[250px] w-full" />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Avaliação Média</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {reportData.performance.avgRating}
                <span className="text-sm text-muted-foreground ml-1">/ 5.0</span>
              </div>
              <p className="text-xs text-muted-foreground">Satisfação dos clientes</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Tempo Médio de Resolução</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {reportData.performance.avgResolutionTime} horas
              </div>
              <p className="text-xs text-muted-foreground">Média global</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Performance por Usuário</CardTitle>
          </CardHeader>
          <CardContent className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={reportData.performance.userPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" orientation="left" stroke="#6366F1" />
                <YAxis yAxisId="right" orientation="right" stroke="#F87171" domain={[0, 5]} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="resolvidos" name="Chamados Resolvidos" fill="#6366F1" />
                <Line yAxisId="right" type="monotone" dataKey="avaliacao" name="Avaliação Média" stroke="#F87171" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cumprimento de SLA por Usuário</CardTitle>
          </CardHeader>
          <CardContent className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={reportData.performance.slaByUser} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={150} />
                <Tooltip formatter={(value) => [`${value}%`, 'Percentual']} />
                <Legend />
                <Bar dataKey="cumprido" name="SLA Cumprido" stackId="a" fill="#34D399" />
                <Bar dataKey="violado" name="SLA Violado" stackId="a" fill="#F87171" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Relatórios</h1>
          <p className="text-muted-foreground mt-1">Análise de métricas e desempenho</p>
        </div>
        <DateRangeFilter
          value={dateRange}
          onChange={setDateRange}
        />
        
        <div className="flex flex-wrap gap-3">
          <ExportButton
            data={reportType === 'tickets' && reportData.tickets ? 
              [
                ...reportData.tickets.byStatus.map(item => ({ tipo: 'Status', categoria: item.name, quantidade: item.value })),
                ...reportData.tickets.byPriority.map(item => ({ tipo: 'Prioridade', categoria: item.name, quantidade: item.valor })),
                ...reportData.tickets.byDay.map(item => ({ tipo: 'Por Dia', data: item.data, abertos: item.abertos, resolvidos: item.resolvidos, atividades: item.atividades }))
              ] :
              reportType === 'performance' && reportData.performance ?
              [
                ...reportData.performance.userPerformance.map(item => ({ tipo: 'Performance', usuario: item.name, resolvidos: item.resolvidos, avaliacao: item.avaliacao })),
                ...reportData.performance.slaByUser.map(item => ({ tipo: 'SLA', usuario: item.name, cumprido: item.cumprido, violado: item.violado }))
              ] : []
            }
            filename={`relatorio_${reportType}_${dateRange}`}
            title={`Relatório de ${reportType === 'tickets' ? 'Chamados' : 'Performance'} - ${dateRange}`}
            type="stats"
            variant="outline"
            disabled={isLoading || !reportData[reportType]}
          />
          <Button 
            variant="outline" 
            className="flex gap-2 items-center"
            onClick={() => loadReportData()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Carregando...' : 'Atualizar'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle>Filtros</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo de Relatório</label>
              <select 
                value={reportType} 
                onChange={(e) => setReportType(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="tickets">Chamados</option>
                <option value="performance">Performance</option>
              </select>
            </div>

            {reportType === 'tickets' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Categoria</label>
                <select 
                  defaultValue="all"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="all">Todas categorias</option>
                  <option value="support">Suporte</option>
                  <option value="development">Desenvolvimento</option>
                  <option value="project">Projeto</option>
                </select>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="md:col-span-3">
          <Tabs defaultValue="visual" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="visual">Gráficos</TabsTrigger>
              <TabsTrigger value="data">Dados</TabsTrigger>
            </TabsList>
            <TabsContent value="visual" className="pt-4">
              {reportType === 'tickets' && renderTicketsReport()}
              {reportType === 'performance' && renderPerformanceReport()}
            </TabsContent>
            <TabsContent value="data">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Dados Detalhados</CardTitle>
                  <ExportButton
                    data={reportType === 'tickets' && reportData.tickets ? 
                      [
                        ...reportData.tickets.byStatus.map(item => ({ tipo: 'Status', categoria: item.name, quantidade: item.value })),
                        ...reportData.tickets.byPriority.map(item => ({ tipo: 'Prioridade', categoria: item.name, quantidade: item.valor })),
                        ...reportData.tickets.byDay.map(item => ({ tipo: 'Por Dia', data: item.data, abertos: item.abertos, resolvidos: item.resolvidos, atividades: item.atividades }))
                      ] :
                      reportType === 'performance' && reportData.performance ?
                      [
                        ...reportData.performance.userPerformance.map(item => ({ tipo: 'Performance', usuario: item.name, resolvidos: item.resolvidos, avaliacao: item.avaliacao })),
                        ...reportData.performance.slaByUser.map(item => ({ tipo: 'SLA', usuario: item.name, cumprido: item.cumprido, violado: item.violado }))
                      ] : []
                    }
                    filename={`dados_detalhados_${reportType}_${dateRange}`}
                    title={`Dados Detalhados - ${reportType === 'tickets' ? 'Chamados' : 'Performance'}`}
                    type="stats"
                    size="sm"
                    disabled={isLoading || !reportData[reportType]}
                  />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center py-8">
                      <h3 className="text-lg font-semibold mb-2">📊 Exportação de Dados</h3>
                      <p className="text-muted-foreground mb-4">
                        Exporte os dados dos relatórios em diferentes formatos
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                        <div className="flex flex-col items-center p-4 border rounded-lg">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
                            <span className="text-green-600 text-xl">📄</span>
                          </div>
                          <h4 className="font-medium">CSV</h4>
                          <p className="text-sm text-muted-foreground text-center">
                            Ideal para Excel e análises
                          </p>
                        </div>
                        
                        <div className="flex flex-col items-center p-4 border rounded-lg">
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-2">
                            <span className="text-green-700 text-xl">📊</span>
                          </div>
                          <h4 className="font-medium">Excel</h4>
                          <p className="text-sm text-muted-foreground text-center">
                            Planilha formatada
                          </p>
                        </div>
                        
                        <div className="flex flex-col items-center p-4 border rounded-lg">
                          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-2">
                            <span className="text-red-600 text-xl">📑</span>
                          </div>
                          <h4 className="font-medium">PDF</h4>
                          <p className="text-sm text-muted-foreground text-center">
                            Relatório para impressão
                          </p>
                        </div>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-4">
                        Use o botão "Exportar" no cabeçalho ou acima para baixar os dados
                      </p>
                    </div>
                    
                    {reportData[reportType] && (
                      <div className="text-sm text-muted-foreground text-center">
                        <p>📈 Dados disponíveis para exportação:</p>
                        <div className="flex justify-center gap-4 mt-2">
                          {reportType === 'tickets' && (
                            <>
                              <span>Status ({reportData.tickets.byStatus.length} categorias)</span>
                              <span>Prioridade ({reportData.tickets.byPriority.length} níveis)</span>
                              <span>Timeline ({reportData.tickets.byDay.length} dias)</span>
                            </>
                          )}
                          {reportType === 'performance' && (
                            <>
                              <span>Usuários ({reportData.performance.userPerformance.length} registros)</span>
                              <span>SLA ({reportData.performance.slaByUser.length} usuários)</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
} 